import { Table } from "@mantine/core"
import { useEffect, useState } from "react"
import { AuthenticatedAppShell } from "../../components/authenticated-app-shell"
import { AppCard, DataTableShell, EmptyState, SectionHeader } from "../../components/ui"
import { type AdminUserSummary, apiGet } from "../../lib/api"
import { formatLocalShortDate } from "../../lib/dates"

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void apiGet<{ users: AdminUserSummary[] }>("/api/admin/users")
      .then((value) => {
        if (!cancelled) setUsers(value.users)
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : "Failed to load users.")
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <AuthenticatedAppShell eyebrow="Admin" title="Users">
      {(session) =>
        session.user?.isAdgiroEmployee ? (
          <>
            {error ? (
              <EmptyState>
                <strong>User list unavailable</strong>
                <br />
                {error}
              </EmptyState>
            ) : null}

            <AppCard>
              <SectionHeader title="Users" description="Adgiro account users across workspaces." />
              <DataTableShell minWidth={760} mt="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>Workspaces</Table.Th>
                    <Table.Th>Provider connections</Table.Th>
                    <Table.Th>Created</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {users.map((user) => (
                    <Table.Tr key={user.userId}>
                      <Table.Td>
                        <strong>{user.name || user.email || user.userId}</strong>
                        <small className="admin-user-meta">{user.email ?? user.userId}</small>
                      </Table.Td>
                      <Table.Td>{user.isAdgiroEmployee ? "Yes" : "No"}</Table.Td>
                      <Table.Td>{user.orgCount}</Table.Td>
                      <Table.Td>{user.providerConnectionCount}</Table.Td>
                      <Table.Td>{formatDate(user.createdAt)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </DataTableShell>
            </AppCard>
          </>
        ) : (
          <EmptyState>
            <strong>Admin unavailable</strong>
            <br />
            Your account does not have Adgiro employee access.
          </EmptyState>
        )
      }
    </AuthenticatedAppShell>
  )
}

function formatDate(value: string) {
  return formatLocalShortDate(value)
}
